import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react'

import { useMobileGalleryTouchZoom } from './useMobileGalleryTouchZoom'

export const useMediaGalleryZoom = ({
	isDesktop,
	isActiveVideo,
	isReviewExpanded,
	setIsChromeHidden,
	setIsReviewExpanded
}: {
	isDesktop: boolean
	isActiveVideo: boolean
	isReviewExpanded: boolean
	setIsChromeHidden: Dispatch<SetStateAction<boolean>>
	setIsReviewExpanded: Dispatch<SetStateAction<boolean>>
}) => {
	const [isDesktopImageZoomed, setIsDesktopImageZoomed] = useState(false)
	const {
		consumeSuppressedMediaClick,
		handleMediaTouchEnd,
		handleMediaTouchMove,
		handleMediaTouchStart,
		isMobileImageZoomed,
		mobileImageRef,
		resetMobileZoom
	} = useMobileGalleryTouchZoom({
		isActiveVideo,
		isDesktop,
		setIsChromeHidden,
		setIsReviewExpanded
	})

	const isGalleryZoomed = isDesktop && isDesktopImageZoomed && !isActiveVideo

	const resetZoom = useCallback(() => {
		setIsDesktopImageZoomed(false)
		resetMobileZoom()
	}, [resetMobileZoom])

	useEffect(() => {
		if (!isDesktop || isActiveVideo) setIsDesktopImageZoomed(false)
	}, [isActiveVideo, isDesktop])

	const handleMediaClick = useCallback(() => {
		if (consumeSuppressedMediaClick()) return
		if (isGalleryZoomed) return

		if (isReviewExpanded) {
			setIsReviewExpanded(false)
			setIsChromeHidden(false)
			return
		}

		setIsChromeHidden(value => !value)
	}, [consumeSuppressedMediaClick, isGalleryZoomed, isReviewExpanded, setIsChromeHidden, setIsReviewExpanded])

	return {
		closeDesktopZoom: () => setIsDesktopImageZoomed(false),
		handleMediaClick,
		handleMediaTouchEnd,
		handleMediaTouchMove,
		handleMediaTouchStart,
		isGalleryZoomed,
		isMobileImageZoomed,
		mobileImageRef,
		openDesktopZoom: () => setIsDesktopImageZoomed(true),
		resetZoom
	}
}
